import { useAuth } from '../context/AuthContext'
import { useGame } from '../context/GameContext'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { useToast } from '../components/ui/Toaster'
import { 
  Send, Sparkles, Star, Trophy,
  ArrowLeft, Flame
} from 'lucide-react'
import { useState } from 'react'

export default function UGCSubmit() {
  const { profile, user } = useAuth()
  const { submitUGC } = useGame()
  const { toast } = useToast()

  const [step, setStep] = useState<'form' | 'preview' | 'success'>('form')
  const [submitting, setSubmitting] = useState(false)

  // Form state
  const [spectrumA, setSpectrumA] = useState({
    label: '',
    left: '',
    right: '',
  })
  const [spectrumB, setSpectrumB] = useState({
    label: '',
    left: '',
    right: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const canSubmit = user && ((profile?.current_streak || 0) >= 3 || !profile)

  const validate = () => {
    const newErrors: Record<string, string> = {}
    
    if (!spectrumA.label.trim()) newErrors['a.label'] = 'Spectrum A label is required'
    if (!spectrumA.left.trim()) newErrors['a.left'] = 'Left label is required'
    if (!spectrumA.right.trim()) newErrors['a.right'] = 'Right label is required'
    if (!spectrumB.label.trim()) newErrors['b.label'] = 'Spectrum B label is required'
    if (!spectrumB.left.trim()) newErrors['b.left'] = 'Left label is required'
    if (!spectrumB.right.trim()) newErrors['b.right'] = 'Right label is required'
    
    // Check for duplicates
    if (spectrumA.left.toLowerCase() === spectrumA.right.toLowerCase()) {
      newErrors['a.same'] = 'Left and right labels must be different'
    }
    if (spectrumB.left.toLowerCase() === spectrumB.right.toLowerCase()) {
      newErrors['b.same'] = 'Left and right labels must be different'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate() || !user) return

    setSubmitting(true)
    try {
      await submitUGC({
        spectrum_a_label: spectrumA.label,
        spectrum_a_left: spectrumA.left,
        spectrum_a_right: spectrumA.right,
        spectrum_b_label: spectrumB.label,
        spectrum_b_left: spectrumB.left,
        spectrum_b_right: spectrumB.right,
      })

      toast({ 
        type: 'success', 
        title: 'Submitted for review!', 
        message: 'Your prompt will be reviewed and may appear in a future daily game.' 
      })
      setStep('success')
    } catch (err) {
      toast({ type: 'error', title: 'Failed to submit', message: err instanceof Error ? err.message : 'Try again' })
    } finally {
      setSubmitting(false)
    }
  }

  const handlePreview = () => {
    if (validate()) {
      setStep('preview')
    }
  }

  if (!canSubmit) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center">
            <Star className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100 mb-2">Unlock UGC Submission</h1>
          <p className="text-surface-500 dark:text-surface-400 mb-6">
            Submit your own Overlap prompts after reaching a <strong>3-day streak</strong>!
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-brand-500 flex items-center justify-center text-white">
                  <Flame className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-surface-900 dark:text-surface-100">Reach 3-day streak</p>
                  <p className="text-sm text-surface-500">Play daily to build your streak</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center text-white">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-surface-900 dark:text-surface-100">Unlock submissions</p>
                  <p className="text-sm text-surface-500">Submit your own prompts for review</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-yellow-500 flex items-center justify-center text-white">
                  <Trophy className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-surface-900 dark:text-surface-100">Get featured</p>
                  <p className="text-sm text-surface-500">Approved prompts appear in the daily game with your credit</p>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-surface-500 dark:text-surface-400 mb-4">
                Your current streak: <span className="font-bold text-brand-600 dark:text-brand-400">{profile?.current_streak || 0}</span> days
              </p>
              <Button onClick={() => window.location.href = '/'}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Daily Game
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (step === 'success') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100 mb-2">Submitted for Review!</h1>
          <p className="text-surface-500 dark:text-surface-400">
            Thanks for contributing to Overlap. Our team will review your prompt and get back to you.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 text-center">
            <Button onClick={() => window.location.href = '/'}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => window.location.href = '/'}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <p className="text-sm text-surface-500 dark:text-surface-400">Submit a Prompt</p>
          <h1 className="text-2xl font-bold text-surface-900 dark:text-surface-100">
            {step === 'form' ? 'Create Your Prompt' : 'Preview Your Prompt'}
          </h1>
        </div>
      </div>

      {step === 'form' && (
        <Card>
          <CardContent className="pt-6 space-y-6">
            <div className="bg-gradient-to-r from-brand-50 to-brand-100 dark:from-brand-900/30 dark:to-brand-800/30 border border-brand-200 dark:border-brand-800 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-brand-600 dark:text-brand-400 mt-0.5" />
                <div>
                  <p className="font-semibold text-brand-900 dark:text-brand-100">How it works</p>
                  <p className="text-sm text-brand-700 dark:text-brand-300 mt-1">
                    Create a prompt with two spectrums. Each spectrum needs a label and two opposing ends.
                    Example: "Pineapple on Pizza" with ends "Crime" and "Delicious".
                  </p>
                </div>
              </div>
            </div>

            {/* Spectrum A */}
            <div className="space-y-4 p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
              <h3 className="font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-brand-500 text-white text-xs flex items-center justify-center font-bold">A</span>
                Spectrum A
              </h3>
              <Input
                label="Spectrum Label"
                placeholder="e.g., Pineapple on Pizza"
                value={spectrumA.label}
                onChange={(e) => setSpectrumA({ ...spectrumA, label: e.target.value })}
                error={errors['a.label']}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Left End (0)"
                  placeholder="e.g., Crime"
                  value={spectrumA.left}
                  onChange={(e) => setSpectrumA({ ...spectrumA, left: e.target.value })}
                  error={errors['a.left'] || errors['a.same']}
                />
                <Input
                  label="Right End (100)"
                  placeholder="e.g., Delicious"
                  value={spectrumA.right}
                  onChange={(e) => setSpectrumA({ ...spectrumA, right: e.target.value })}
                  error={errors['a.right'] || errors['a.same']}
                />
              </div>
            </div>

            {/* Spectrum B */}
            <div className="space-y-4 p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
              <h3 className="font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center font-bold">B</span>
                Spectrum B
              </h3>
              <Input
                label="Spectrum Label"
                placeholder="e.g., Hot Dog = Sandwich?"
                value={spectrumB.label}
                onChange={(e) => setSpectrumB({ ...spectrumB, label: e.target.value })}
                error={errors['b.label']}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Left End (0)"
                  placeholder="e.g., Absolutely Not"
                  value={spectrumB.left}
                  onChange={(e) => setSpectrumB({ ...spectrumB, left: e.target.value })}
                  error={errors['b.left'] || errors['b.same']}
                />
                <Input
                  label="Right End (100)"
                  placeholder="e.g., Technically Yes"
                  value={spectrumB.right}
                  onChange={(e) => setSpectrumB({ ...spectrumB, right: e.target.value })}
                  error={errors['b.right'] || errors['b.same']}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="secondary" onClick={handlePreview} className="flex-1">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Preview
              </Button>
              <Button onClick={handleSubmit} loading={submitting} className="flex-1">
                <Send className="w-4 h-4 mr-2" />
                Submit for Review
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'preview' && (
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-4">Preview</h3>
              <div className="space-y-6">
                <PromptPreviewCard
                  label={spectrumA.label}
                  left={spectrumA.left}
                  right={spectrumA.right}
                  color="brand"
                />
                <PromptPreviewCard
                  label={spectrumB.label}
                  left={spectrumB.left}
                  right={spectrumB.right}
                  color="purple"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setStep('form')} className="flex-1">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Edit
            </Button>
            <Button onClick={handleSubmit} loading={submitting} className="flex-1">
              <Send className="w-4 h-4 mr-2" />
              Submit for Review
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function PromptPreviewCard({ label, left, right, color }: { label: string, left: string, right: string, color: 'brand' | 'purple' }) {
  const colors = {
    brand: 'from-brand-500 to-brand-600',
    purple: 'from-purple-500 to-purple-600',
  } as const

  return (
    <div className="p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
      <p className="font-semibold text-surface-900 dark:text-surface-100 mb-3">{label}</p>
      <div className="flex items-center justify-between text-sm text-surface-500 mb-2">
        <span>{left}</span>
        <span>{right}</span>
      </div>
      <div className="h-2 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r rounded-full" style={{ background: colors[color] }} />
      </div>
    </div>
  )
}